import React from "react";
import { Flex, Image, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { resolveImageUrl } from "@/utils/image";

// 인기 브랜드/즐겨찾기 브랜드 두 응답에서 함께 쓰이므로 응답 타입 대신 원시값을 받는다.
interface Props {
  brandName: string;
  logoUrl?: string | null;
  // 순위는 응답 배열 순서에서 파생되므로 호출부가 전달한다.
  rank: number;
  // 브랜드 필터(brandId__in)에 이 브랜드가 걸려 있는지 — 여러 카드가 동시에 선택될 수 있다.
  isSelected: boolean;
  // 브랜드 필터 옵션(filter-info)에 없는 브랜드는 상품을 걸러 볼 수 없다 —
  // 카드와 상품 보기 버튼을 비활성화한다(즐겨찾기 별은 그대로 동작).
  isFilterable: boolean;
  isFavorite: boolean;
  onToggleFilter: () => void;
  onToggleFavorite: () => void;
}

function PathPickSearchProductsPopularBrandCard({
  brandName,
  logoUrl,
  rank,
  isSelected,
  isFilterable,
  isFavorite,
  onToggleFilter,
  onToggleFavorite,
}: Props) {
  const { t } = useI18nHelper();

  // 카드 전체가 브랜드 필터 버튼이라 별 클릭이 위로 전파되면 필터까지 걸린다.
  const handleToggleFavorite = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggleFavorite();
  };

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      padding={"12px"}
      border={isSelected ? "1px solid #1272EF" : "0.5px solid #D4D4D1"}
      borderRadius={"12px"}
      background={isFilterable ? "#FFFFFF" : "#F7F9FA"}
      cursor={isFilterable ? "pointer" : "default"}
      onClick={isFilterable ? onToggleFilter : undefined}
      _hover={
        isFilterable ? { boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.10)" } : {}
      }
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <Flex gap={"8px"} alignItems={"center"} overflow={"hidden"}>
          <Flex
            boxSize={"40px"}
            flexShrink={0}
            alignItems={"center"}
            justifyContent={"center"}
            borderRadius={"full"}
            border={"0.5px solid #D4D4D1"}
            background={"#F7F9FA"}
            overflow={"hidden"}
          >
            <Image
              src={resolveImageUrl(logoUrl)}
              boxSize={"100%"}
              objectFit={"contain"}
              alt={brandName}
              fallback={
                <Text textStyle={"body-md-bold"} color={"#444440"}>
                  {brandName.charAt(0)}
                </Text>
              }
            />
          </Flex>
          <Flex flexDirection={"column"} overflow={"hidden"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {`#${rank}`}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#444440"} noOfLines={1}>
              {brandName}
            </Text>
          </Flex>
        </Flex>
        <Flex
          as={"button"}
          type={"button"}
          alignItems={"center"}
          justifyContent={"center"}
          padding={"4px"}
          flexShrink={0}
          onClick={handleToggleFavorite}
          aria-label={t(
            isFavorite
              ? "path_pick:search_products.popular.favorite_brand_remove"
              : "path_pick:search_products.popular.favorite_brand_add"
          )}
        >
          <CustomIcon
            src={isFavorite ? "star-solid" : "star"}
            width={20}
            height={20}
            color={isFavorite ? "#1272EF" : "#8F8F8C"}
          />
        </Flex>
      </Flex>
      {isFilterable ? (
        <AppButton
          variant={isSelected ? "primary" : "white"}
          width={"100%"}
          onClick={onToggleFilter}
          // Chakra Button 기본 200ms 트랜지션을 꺼서 텍스트/아이콘(즉시 변경)과 배경/글자색이 동시에 전환되게 한다.
          chakraProps={{ transitionDuration: "0s" }}
        >
          <Flex gap={"4px"} alignItems={"center"} justifyContent={"center"}>
            <Text textStyle={"body-sm-bold"}>
              {isSelected
                ? t("path_pick:search_products.popular.viewing_brand_products")
                : t("path_pick:search_products.popular.view_brand_products")}
            </Text>
            <CustomIcon
              src={"chevron-right"}
              width={20}
              height={20}
              color={isSelected ? "#FFFFFF" : undefined}
            />
          </Flex>
        </AppButton>
      ) : (
        // 필터에 걸 수 없는 브랜드 — 버튼 자리를 비우면 카드 높이가 들쭉날쭉해지므로
        // 같은 자리에 비활성 버튼으로 이유를 적어 둔다.
        <AppButton variant={"white"} width={"100%"} isDisabled={true}>
          <Text textStyle={"body-sm-bold"}>
            {t("path_pick:search_products.popular.unavailable_brand")}
          </Text>
        </AppButton>
      )}
    </Flex>
  );
}

export default PathPickSearchProductsPopularBrandCard;
